"use client";
import clsx from "clsx";
import { type Control, type FieldPath, type FieldValues, useController } from "react-hook-form";

type Props<T extends FieldValues> = {
  id: string;
  label: string;
  name: FieldPath<T>;
  control: Control<T>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  items: Array<{
    text: string;
    callbackValue: string;
  }>;
  handleChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};
export const Dropdown = <T extends FieldValues>({
  id,
  label,
  control,
  disabled,
  placeholder,
  className,
  name,
  items,
  handleChange,
}: Props<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={clsx("text-xs font-normal font-sans flex flex-nowrap", {
          "text-no": error,
          "text-tx-1": !error,
        })}
      >
        {label}
      </label>
      <div className="mt-2 relative">
        <svg
          className="absolute right-[13px] top-[13px] rotate-180 select-none pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        >
          <path fill="currentColor" d="m12 10.83-4.95 4.95-1.41-1.42L12 8l6.36 6.36-1.41 1.42L12 10.83Z" />
        </svg>
        <select
          {...field}
          id={id}
          onChange={(e) => {
            if (handleChange) {
              handleChange(e);
            }
            field.onChange(e);
          }}
          disabled={disabled}
          className={clsx(
            "bg-white rounded-lg border py-3 px-4 w-full text-base font-normal placeholder:text-[#B8B8B8] appearance-none",
            {
              "text-[#C92A2A] border-[#C92A2A]": error,
              "text-[#22201F] border-[#E5E5E5]": !error && field.value !== "",
              "text-[#B8B8B8] border-[#E5E5E5]": !error && field.value === "",
            },
          )}
        >
          <option value={undefined} hidden className="hidden" />
          {placeholder && (
            <option value="" hidden disabled className="hidden">
              {placeholder}
            </option>
          )}
          {items.map((item) => (
            <option key={item.callbackValue} value={item.callbackValue}>
              {item.text}
            </option>
          ))}
        </select>
      </div>
      {error?.message && <p className="text-[#C92A2A] text-xs leading-5 mt-1">{error.message}</p>}
    </div>
  );
};
